// src/features/admin/PianiFunzioni.jsx
import React, { useEffect, useState } from "react";
import { Grid3X3, Loader2 } from "lucide-react";

const PLAN_LABELS = {
  FREE: "Norma Free",
  SYNCRO: "Norma Start",
  IOT_GUARDIAN: "Norma Pro",
  CUBO_PREDICTIVE: "Norma Enterprise",
};

export default function PianiFunzioni({ isSuper }) {
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [permessi, setPermessi] = useState([]);
  const [plans, setPlans] = useState([]);
  const [mapping, setMapping] = useState({});
  const [savingPlan, setSavingPlan] = useState(null);
  const [dirtyPlans, setDirtyPlans] = useState({}); // {planCode: true}

  useEffect(() => {
    if (!isSuper) return;

    async function load() {
      setLoading(true);
      setErrore("");
      try {
        const res = await fetch("/api/admin/plan_features_list.php", {
          credentials: "include",
        });
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error("Risposta non valida da plan_features_list:\n" + text);
        }

        if (!res.ok || !data.ok) {
          throw new Error(data.message || "Errore caricamento plan_features.");
        }

        setPermessi(Array.isArray(data.permessi) ? data.permessi : []);
        setPlans(Array.isArray(data.plans) ? data.plans : []);
        setMapping(data.mapping || {});
      } catch (err) {
        setErrore(err.message);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [isSuper]);

  const togglePerm = (planCode, permCode) => {
    setMapping((prev) => {
      const current = prev[planCode] || [];
      const exists = current.includes(permCode);
      const next = exists
        ? current.filter((c) => c !== permCode)
        : [...current, permCode];

      return {
        ...prev,
        [planCode]: next,
      };
    });

    setDirtyPlans((prev) => ({ ...prev, [planCode]: true }));
  };

  const savePlan = async (planCode) => {
    setSavingPlan(planCode);
    setErrore("");

    try {
      const res = await fetch("/api/admin/plan_features_save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_code: planCode,
          perm_codes: mapping[planCode] || [],
        }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Risposta non valida da plan_features_save:\n" + text);
      }

      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Errore salvataggio piano.");
      }

      setDirtyPlans((prev) => {
        const copy = { ...prev };
        delete copy[planCode];
        return copy;
      });
    } catch (err) {
      setErrore(err.message);
      alert("Errore salvataggio: " + err.message);
    } finally {
      setSavingPlan(null);
    }
  };

  if (!isSuper) {
    return (
      <div className="p-3 text-sm text-slate-600">
        Solo il superadmin può gestire le funzionalità dei piani.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-slate-600">
        <Loader2 size={16} className="animate-spin" /> Caricamento piani &amp;
        funzioni…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Grid3X3 size={18} />
          Funzionalità per piano
        </h2>
        <p className="text-sm text-slate-500">
          Seleziona quali moduli / funzionalità sono inclusi in ogni piano
          (Norma Free, Norma Start, Norma Pro, Norma Enterprise).
        </p>
      </div>

      {errore && (
        <div className="bg-amber-100 text-amber-900 px-3 py-2 rounded text-sm">
          {errore}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const selectedPerms = mapping[plan] || [];
          const isDirty = !!dirtyPlans[plan];

          return (
            <div
              key={plan}
              className="bg-white border rounded-xl shadow-sm p-3 flex flex-col"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs uppercase text-slate-500">
                    Piano
                  </div>
                  <div className="font-semibold">
                    {PLAN_LABELS[plan] || plan}
                  </div>
                </div>
                <span className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">
                  {selectedPerms.length} funzioni
                </span>
              </div>

              <div className="flex-1 border-t pt-2 mt-1 space-y-1 max-h-72 overflow-auto">
                {permessi.map((p) => {
                  const checked = selectedPerms.includes(p.code);
                  return (
                    <label
                      key={p.code}
                      className="flex items-center gap-2 text-xs text-slate-700"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={checked}
                        onChange={() => togglePerm(plan, p.code)}
                      />
                      <span>{p.label}</span>
                    </label>
                  );
                })}
              </div>

              <div className="pt-2 mt-2 border-t flex justify-between items-center">
                {isDirty && (
                  <span className="text-[11px] text-amber-600">
                    Modifiche non salvate
                  </span>
                )}
                <button
                  onClick={() => savePlan(plan)}
                  disabled={savingPlan === plan}
                  className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {savingPlan === plan ? "Salvo…" : "Salva piano"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
